// src/prototype/components/oir/report/OIRSectionExecutiveSummary.tsx
// ============================================================
// UX Improvements (March 2026):
//   - Narrative-led structure: Intro → Top 5 Findings →
//     Transition paragraph → KPI metric cards → Top Initiatives
//   - KPI cards support the narrative, they do not lead it
//   - Large numeric value on top, explanatory label below
//   - Dynamic findings from top10 data (categories, quick wins)
//   - Top Recommended Initiatives: prominent ROI, effort chip,
//     time-to-value visibility
// ============================================================

import React from 'react';
import {
  TrendingUp,
  Layers,
  Star,
  DollarSign,
  Clock,
  Zap,
  CheckCircle2,
} from 'lucide-react';

// ── ROI formatting (match Use Case Explorer: 2 decimal places) ─────────────
const formatRoiValue = (v: number): string =>
  v < 100 ? v.toFixed(2) : (v / 1_000_000).toFixed(2);

const formatRoiRange = (low: number, high: number): string =>
  `$${formatRoiValue(low)}–$${formatRoiValue(high)}M`;

// ── Types ──────────────────────────────────────────────────────────────────
interface Opportunity {
  id: string;
  rank: number;
  title: string;
  category: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
  timeToValue: string;
  roiEstimate: { low: number; high: number; unit?: string };
  confidence: string | number;
  /** Same badge Use Case Explorer shows when present (value_band). */
  valueBand?: string;
}

export interface OIRSectionExecutiveSummaryProps {
  clientName: string;
  portfolioRangeLabel: string;
  shortlistedRangeLabel: string;
  selectedCount: number;
  totalOpportunities: number;
  top10: Opportunity[];
  /** When provided, shown directly as the Top 3 Recommended (from the API's top3recommended field). */
  top3Opps?: Opportunity[];
  selectedOpportunities: Opportunity[];
}

// ── Static strategic themes ────────────────────────────────────────────────
const STRATEGIC_THEMES = [
  'Operational Efficiency',
  'Revenue Integrity',
  'Workforce Optimization',
  'Patient Flow',
  'Clinical Quality',
  'Supply Chain',
];

// ── Effort / value-band helpers (align with Use Case Explorer badges) ─────
const effortChip = (effort: string): string => {
  const map: Record<string, string> = {
    Low: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
    Medium: 'bg-amber-50  text-amber-700  border border-amber-100',
    High: 'bg-rose-50   text-rose-600   border border-rose-100',
  };
  return map[effort] ?? 'bg-gray-100 text-gray-600 border border-gray-200';
};

const effortLabel = (effort: string): string => {
  const map: Record<string, string> = {
    Low: 'Quick Win',
    Medium: 'Moderate',
    High: 'High',
  };
  return map[effort] ?? effort;
};

const valueBandChip = (band: string): string => {
  const b = band.trim().toLowerCase();
  if (b === 'significant' || b === 'high') return 'bg-rose-50 text-rose-700 border border-rose-100';
  if (b === 'moderate') return 'bg-amber-50 text-amber-700 border border-amber-100';
  if (b === 'low' || b === 'emerging') return 'bg-slate-50 text-slate-600 border border-slate-200';
  return 'bg-gray-100 text-gray-600 border border-gray-200';
};

const isQuickWin = (effort: string) => effort === 'Low';

// ── Metric Card ────────────────────────────────────────────────────────────
const MetricCard: React.FC<{
  value: React.ReactNode;
  label: string;
  sub?: string;
  accent?: boolean;
  icon: React.ReactNode;
}> = ({ value, label, sub, accent, icon }) => (
  <div
    className={`rounded-xl p-4 flex flex-col gap-1.5 ${accent
        ? 'bg-[#FF6E4C]/[0.08] border border-[#FF6E4C]/20'
        : 'bg-[#18365E]/[0.05] border border-[#18365E]/10'
      }`}
  >
    <div className="flex items-center gap-1.5">
      <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${accent ? 'bg-[#FF6E4C]/15' : 'bg-[#18365E]/10'
        }`}>
        {icon}
      </div>
      <span className="text-[9px] font-semibold text-[#717182] uppercase tracking-wide leading-tight">
        {label}
      </span>
    </div>
    <p className={`text-2xl font-bold leading-none ${accent ? 'text-[#FF6E4C]' : 'text-[#18365E]'}`}>
      {value}
    </p>
    {sub && (
      <p className="text-[10px] text-[#717182] leading-tight">{sub}</p>
    )}
  </div>
);

// ── Initiative Card ────────────────────────────────────────────────────────
const InitiativeCard: React.FC<{
  opp: Opportunity;
  index: number;
}> = ({ opp, index }) => {
  const band = opp.valueBand?.trim();
  const badgeLabel = band || effortLabel(opp.implementationEffort);
  const badgeClass = band ? valueBandChip(band) : effortChip(opp.implementationEffort);
  const showQuickWin = !band && isQuickWin(opp.implementationEffort);

  return (
  <div className="border border-[#e5e7eb] rounded-xl p-4 bg-white flex flex-col gap-3 hover:shadow-sm transition-shadow">
    <div className="flex items-start gap-2.5">
      <div className="w-7 h-7 rounded-full bg-[#18365E] flex items-center justify-center shrink-0 mt-0.5">
        <span className="text-[11px] font-bold text-white">{index + 1}</span>
      </div>
      <p className="text-sm font-semibold text-[#18365E] leading-snug">{opp.title}</p>
    </div>
    <div className="flex items-baseline gap-1.5 pl-0.5">
      <TrendingUp className="w-3.5 h-3.5 text-[#FF6E4C] shrink-0 mt-0.5" />
      <span className="text-base font-bold text-[#FF6E4C] leading-none">
        {formatRoiRange(opp.roiEstimate.low, opp.roiEstimate.high)}
      </span>
      <span className="text-[10px] text-[#717182]">/yr</span>
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badgeClass}`}>
        {showQuickWin && (
          <Zap className="w-2.5 h-2.5 inline-block mr-0.5 -mt-px" />
        )}
        {badgeLabel}
      </span>
      <div className="flex items-center gap-1 text-[10px] text-[#717182]">
        <Clock className="w-2.5 h-2.5 shrink-0" />
        <span>{opp.timeToValue}</span>
      </div>
    </div>
  </div>
  );
};

// ── Strategic Finding Row ──────────────────────────────────────────────────
const FindingRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-start gap-2.5">
    <CheckCircle2 className="w-4 h-4 text-[#FF6E4C] shrink-0 mt-px" />
    <p className="text-sm text-[#18365E]/85 leading-relaxed">{children}</p>
  </div>
);

// ── Component ──────────────────────────────────────────────────────────────
export const OIRSectionExecutiveSummary: React.FC<OIRSectionExecutiveSummaryProps> = ({
  clientName,
  portfolioRangeLabel,
  shortlistedRangeLabel,
  selectedCount,
  totalOpportunities,
  top10,
  top3Opps,
  selectedOpportunities,
}) => {
  // Showcase: when the user has selected finalists, ALWAYS show those selections.
  // Never substitute API top_recommended while selectedCount > 0 — that caused
  // "Top Selected Initiatives" to list unrelated recommended use cases.
  const hasUserSelection = selectedCount > 0 || selectedOpportunities.length > 0;
  const showcaseOpps = hasUserSelection
    ? selectedOpportunities.slice(0, 3)
    : top3Opps && top3Opps.length > 0
      ? top3Opps.slice(0, 3)
      : top10.slice(0, 3);

  const showcaseHeading = hasUserSelection
    ? selectedOpportunities.length === 1 || selectedCount === 1
      ? 'Selected Initiative'
      : selectedOpportunities.length === 2 || selectedCount === 2
        ? 'Top Selected Initiatives'
        : 'Top 3 Selected Initiatives'
    : 'Top 3 Recommended Initiatives';

  // Dynamic findings
  const quickWinCount = top10.filter(o => o.implementationEffort === 'Low').length;

  const categoryCounts: Record<string, number> = {};
  top10.forEach(o => { categoryCounts[o.category] = (categoryCounts[o.category] || 0) + 1; });
  const topCategories = Object.entries(categoryCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([cat]) => cat);

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-5 mb-5">

      {/* ── Section header ── */}
      <div className="mb-4">
        <h2 className="text-base font-bold text-[#18365E]">Executive Summary</h2>
        <p className="text-[11px] text-[#717182] mt-0.5">
          Portfolio Strategy snapshot — strategic findings, valuation, and selected initiatives
        </p>
      </div>

      {/* ── 1. Intro Paragraph ── */}
      <p className="text-sm text-[#18365E]/80 leading-relaxed mb-5">
        The Portfolio Strategy analysis evaluated the opportunity landscape across strategic
        value, implementation feasibility, and financial impact. These insights identify the
        initiatives best positioned to improve{' '}
        <strong>{clientName}</strong>'s organizational performance and support portfolio-level
        decision making.
      </p>

      {/* ── 2. Top Strategic Findings ── */}
      <div className="mb-5">
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">
          Top Strategic Findings
        </p>
        <div className="space-y-2.5 bg-[#F5F7FA] rounded-xl p-4 border border-[#e5e7eb]">
          <FindingRow>
            The evaluated opportunity portfolio represents{' '}
            <strong>{portfolioRangeLabel}</strong>{' '}
            in potential annual value
            across <strong>{totalOpportunities}</strong> assessed initiatives.
          </FindingRow>
          <FindingRow>
            {topCategories.length >= 2 ? (
              <><strong>{topCategories[0]}</strong> and <strong>{topCategories[1]}</strong> represent
              the largest opportunity clusters in the ranked portfolio.</>
            ) : (
              <>The top opportunity clusters represent high-value domains aligned to your strategic priorities.</>
            )}
          </FindingRow>
          <FindingRow>
            {quickWinCount > 0 ? (
              <><strong>{quickWinCount}</strong>{' '}
              initiative{quickWinCount !== 1 ? 's' : ''} emerged
                as high-confidence Quick Win{quickWinCount !== 1 ? 's' : ''} — high value with lower
                implementation complexity.</>
            ) : (
              <>The top initiatives demonstrate strong financial returns with manageable implementation timelines.</>
            )}
          </FindingRow>
          <FindingRow>
            {selectedCount > 0 ? (
              <>The recommended portfolio of <strong>{selectedCount}</strong> selected
                initiative{selectedCount !== 1 ? 's' : ''} represents{' '}
                <strong>{shortlistedRangeLabel}</strong> in expected annual value.</>
            ) : (
              <>The recommended portfolio represents <strong>{portfolioRangeLabel}</strong> in
                combined annual value potential from the top-ranked opportunities.</>
            )}
          </FindingRow>
          <FindingRow>
            Several additional initiatives fall into the Strategic Investment quadrant, representing
            high-value longer-term opportunities suitable for phased execution planning.
          </FindingRow>
        </div>
      </div>

      {/* ── 3. Transition Paragraph ── */}
      <p className="text-sm text-[#18365E]/70 leading-relaxed mb-5">
        The sections below provide deeper insight into the strategic context, opportunity landscape,
        financial impact, and prioritization framework used to evaluate these initiatives.
      </p>

      {/* ── 4. Portfolio Metrics — 2×2 grid ── */}
      <div className="mb-5">
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">
          Portfolio Metrics
        </p>
        <div className="grid grid-cols-2 gap-3">
          <MetricCard
            icon={<TrendingUp className="w-3.5 h-3.5 text-[#FF6E4C]" />}
            value={portfolioRangeLabel}
            label="Portfolio Value"
            sub="Top opportunities · annual potential"
            accent
          />
          <MetricCard
            icon={<Layers className="w-3.5 h-3.5 text-[#18365E]" />}
            value={String(totalOpportunities)}
            label="Opportunities Evaluated"
            sub="Ranked by strategic fit & ROI"
          />
          <MetricCard
            icon={<Star className="w-3.5 h-3.5 text-[#18365E]" />}
            value={selectedCount > 0 ? String(selectedCount) : '—'}
            label="Selected Initiatives"
            sub={selectedCount > 0 ? 'Marked as finalist' : 'None selected yet'}
          />
          <MetricCard
            icon={<DollarSign className="w-3.5 h-3.5 text-[#FF6E4C]" />}
            value={selectedCount > 0 ? shortlistedRangeLabel : '—'}
            label="Selected Value"
            sub={selectedCount > 0 ? 'Annual · shortlisted portfolio' : 'Select initiatives to see'}
            accent={selectedCount > 0}
          />
        </div>
      </div>

      {/* ── 5. Top Recommended Initiatives ── */}
      <div className="mb-5">
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-3">
          {showcaseHeading}
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {showcaseOpps.length > 0 ? (
            showcaseOpps.map((opp, i) => (
              <InitiativeCard key={opp.id} opp={opp} index={i} />
            ))
          ) : hasUserSelection ? (
            <p className="text-sm text-[#717182] col-span-full py-2">
              Loading your selected initiatives…
            </p>
          ) : null}
        </div>
      </div>

      {/* ── 6. Strategic Themes ── */}
      <div>
        <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-2">
          Strategic Themes Covered
        </p>
        <div className="flex flex-wrap gap-2">
          {STRATEGIC_THEMES.map((theme) => (
            <span
              key={theme}
              className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-[#18365E]/[0.06] text-[#18365E] border border-[#18365E]/10"
            >
              {theme}
            </span>
          ))}
        </div>
      </div>

    </div>
  );
};

export default OIRSectionExecutiveSummary;